import { HStack, View, VStack } from "@gluestack-ui/themed";
import { BarChartProps } from "@repo/core/components/chart/ReportCharts/BarChart/types";
import { ReportChartsController } from "@repo/core/components/chart/ReportCharts/controller";
import {
  normalizeLineDataValues,
  normalizeVisualizationDataValues
} from "@repo/core/components/chart/ReportCharts/utils";
import { Text } from "@repo/core/components/core/Text";
import {
  VictoryChart,
  VictoryGroup,
  VictoryBar,
  VictoryLine,
  VictoryScatter,
  VictoryAxis,
  VictoryContainer
} from "@repo/core/components/victory";
import { useDimensions } from "@repo/core/hooks/useDimensions";

export const BarChart = ({
  isStackedBars,
  data,
  maxValue,
  maxDomain,
  numberOfGridLines = 10,
  showRightLabels,
  lineData,
  lineDataPercentage,
  leftLabelsTransform,
  rightLabelsTransform,
  customStyles
}: BarChartProps) => {
  const { tickValues, isMobile, styles, indicatorsData } =
    ReportChartsController({
      data,
      maxValue,
      numberOfGridLines,
      isStackedBars,
      customStyles
    });

  const { width } = useDimensions();
  return (
    <VStack
      bg="$white"
      borderWidth={1}
      borderColor="$secondary200"
      borderRadius={isMobile ? 0 : "$lg"}
      pb="$4"
    >
      <VictoryChart
        domainPadding={{ x: isMobile ? 40 : customStyles?.domainPadding || 48 }}
        domain={{ y: [0, maxDomain] }}
        height={isMobile ? 250 : 300}
        width={isMobile ? width : width - 300}
        padding={styles.chartPadding}
        containerComponent={
          <VictoryContainer responsive style={{ paddingInline: 16 }} />
        }
      >
        <VictoryAxis
          crossAxis
          style={styles.bottomAxisStyle}
          orientation="bottom"
        />

        <VictoryAxis
          dependentAxis
          style={styles.leftAxisStyle}
          tickValues={tickValues}
          orientation="left"
          tickFormat={(tick) =>
            leftLabelsTransform ? leftLabelsTransform(tick) : tick
          }
        />
        {showRightLabels && (
          <VictoryAxis
            dependentAxis
            style={styles.rightAxisStyle}
            tickValues={tickValues}
            orientation="right"
            tickFormat={(tick) =>
              rightLabelsTransform ? rightLabelsTransform(tick) : tick
            }
          />
        )}
        <VictoryGroup
          offset={isStackedBars ? 0 : customStyles?.groupOffset || 20}
          style={styles.groupStyle}
        >
          {data.map((data) => (
            <VictoryBar
              data={normalizeVisualizationDataValues(
                isMobile
                  ? data.visualizationData.slice(0, 3)
                  : data.visualizationData
              )}
              style={{
                data: { fill: data.color }
              }}
              cornerRadius={{ topLeft: 2, topRight: 2 }}
            />
          ))}
        </VictoryGroup>

        <VictoryLine
          data={normalizeLineDataValues(lineData?.values || [])}
          style={{
            data: { stroke: lineData?.color, strokeWidth: 2 }
          }}
        />

        <VictoryScatter
          size={6}
          data={normalizeLineDataValues(lineData?.values || [])}
          style={{
            data: { fill: lineData?.color, strokeWidth: 1 },
            labels: {
              fontSize: lineDataPercentage ? 8 : 12,
              padding: 6
            }
          }}
          labels={({ datum }) =>
            lineDataPercentage
              ? `${Math.round((datum.y / maxValue) * 100)}%`
              : datum.y
          }
        />
      </VictoryChart>

      <HStack justifyContent="center" gap="$6" flexWrap="wrap">
        {indicatorsData?.map((indicatorData) => (
          <HStack gap="$1" alignItems="center">
            <View bg={indicatorData.color} h="$4" w="$4" borderRadius={4} />
            <Text fontSize="$xs">{indicatorData.name}</Text>
          </HStack>
        ))}

        <HStack gap="$1" alignItems="center">
          <View bg={lineData?.color} h="$0.5" w="$4" borderRadius={4} />
          <Text fontSize="$xs">{lineData?.name}</Text>
        </HStack>
      </HStack>
    </VStack>
  );
};
